import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NbGlobalPhysicalPosition, NbSelectComponent, NbToastrService, NbWindowRef, NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { User } from '../../../../models/user.model';
import { Role } from '../../../../models/role.model';
import { MockService } from '../../../../services/mock.service';
import { Restaurant } from '../../../../models/restaurant.model';
import { DialogComponent } from "../../../../shared/dialog/dialog.component";
import { UserService } from '../../../../services/user.service';
import { RestaurantLocation } from '../../../../models/restaurantLocation.model';

@Component({
  selector: 'ngx-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent implements OnInit, OnDestroy {
  @Input() user: User;
  @Input() add: boolean;
  @Input() profile: boolean;
  @ViewChild("form") form: NgForm;
  @ViewChild("selekt") selekt: NbSelectComponent;
  @ViewChild("selectLocation") locationSelect: NbSelectComponent;
  editedUser: User;
  reader = new FileReader();
  subscriptions: Subscription = new Subscription();
  positions = NbGlobalPhysicalPosition;
  photoChanged: boolean = false;
  addingCategory: boolean = false;
  photo: File;
  roles: Role[];
  restaurants: Restaurant[];
  locations: RestaurantLocation[];
  selectedRestaurants: Restaurant[];

  constructor(
    private windowRef: NbWindowRef,
    private toastrService: NbToastrService,
    private translate: TranslateService,
    private roleService: MockService,
    private dialogService: NbDialogService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.editedUser = JSON.parse(JSON.stringify(this.user));
    
    this.roleService.getRoles().subscribe(roles => {
      this.roles = roles.filter(role => role.name !== 'admin');
    });

    this.roleService.getRestaurants().subscribe((restaurants: Restaurant[]) => {
      this.restaurants = restaurants;
      this.locations = this.restaurants.reduce((acc: RestaurantLocation[], r: Restaurant) => {
          return acc.concat(r.locations || []);
        }, []);

      let user_restaurants: Restaurant[] = []
      let user_locations: RestaurantLocation[] = [];

      if( this.editedUser.role_name == "admin") {
        user_restaurants = this.restaurants;
        user_locations = this.locations;
      } 
      else 
      {
        user_restaurants = restaurants.filter( ( restaurant) => {
          return this.user.restaurant_ids?.includes( restaurant.id);
        });

        user_locations = user_restaurants.reduce( ( acc: RestaurantLocation[], r: Restaurant) => 
        {
          return acc.concat( r.locations || []);
        }, []).filter( ( rl: RestaurantLocation) => 
          {
            return this.user.location_ids?.includes( rl.id);
          });
      }

      this.editedUser.restaurant_ids = user_restaurants.map(((r) => r.id));
      this.selectedRestaurants = user_restaurants;

      
      const location_ids: string[] = [];

      user_restaurants.forEach(restaurant => 
      {
        const restaurantLocationIds = (restaurant.locations || []).map(loc => loc.id);

        const hasAnyLocation = restaurantLocationIds.some(id => this.user.location_ids?.includes(id));

        if (hasAnyLocation) {
          location_ids.push(...restaurantLocationIds.filter(id =>
            this.user.location_ids?.includes(id)
          ));
        } else {
          location_ids.push(`${restaurant.id}:all`);
        }
      });
      
      this.cdr.detectChanges();
      setTimeout(() => {
        this.editedUser.location_ids = location_ids;
        this.cdr.detectChanges();
      });
    }, (error) => {
      this.toastrService.warning(
        this.translate.instant("UTILS.WARNING"),
        this.translate.instant("UTILS.ERROR_INFO", {
          subject: this.translate.instant("UTILS.LABELS.RESTAURANTS"),
          operation: this.translate.instant("UTILS.DOWNLOADING"),
        })
      );
    });
  }

  onFileSelected(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      this.photo = event.target.files[0];
      this.reader.onload = (e: any) => {
        this.editedUser.photo = e.target.result;
        this.photoChanged = true;
      };
      this.reader.readAsDataURL(this.photo);
    }
  }

  deleteUser() {
    this.dialogService.open(DialogComponent, {
			context: {
				title: this.translate.instant("UTILS.DELETE_BUTTON", {
					subject: this.translate.instant("USERS.USER_EDIT.USER"),
				}) + ` '${this.user.name}'`,
				content: this.translate.instant("UTILS.DELETE_CONFIRM", {
					subject: ': ' + this.translate.instant("USERS.USER_EDIT.USER") + ` '${this.user.name}'`,
				}),
        withConfirmation: true
			},
      closeOnBackdropClick: false
		}).onClose.subscribe((confirmed) => {
      if(confirmed) {
        this.handleUserOperation("delete");
      }
    })
  }

  saveChanges() {
    if (!this.form.pristine || this.photoChanged) {
      this.dialogService.open(DialogComponent, {
        context: {
          title: this.translate.instant("UTILS.CANCEL_CONFIRM"),
          withConfirmation: true
        },
        closeOnBackdropClick: false
      }).onClose.subscribe((confirmed) => {
        if(confirmed) {
          if (this.editedUser && this.form.valid) {
            this.handleUserOperation(this.add ? "add" : "edit");
          } else {
            this.toastrService.warning(
              this.translate.instant("UTILS.WARNING"),
              this.translate.instant("UTILS.INVALID")
            );
          }
        } else {
          this.handleUserOperation();
        }
      })
    } else {
      this.handleUserOperation();
      return;
    }
  }

  handleUserOperation(operation?: string) {
    this.performUserOperation({
      user: this.editedUser,
      photo: this.photo || null,
      operation: operation || null,
    });
  }

  performUserOperation(res: { user: User; photo: File; operation: string }) {
    if(!res.operation){
      this.windowRef.close();
    }
		const userData: {
			id?: string, name?: string, role_name?: string, email?: string, card_nr?: string, password?: string, photo_file?: File, restaurant_ids?: string[], location_ids?: string[]
		} = {
      id: res.user.id || null,
      name: res.user.name,
      role_name: res.user.role_name,
      email: res.user.email,
      card_nr: res.user.card_nr,
      password: res.user.password,
			restaurant_ids: res.user.restaurant_ids,
      location_ids: res.user.location_ids.filter((loc_id) => !loc_id.includes(':all')),
		};
		if (res.photo !== undefined) {
			userData.photo_file = res.photo;
		}

		switch (res.operation) {
			case "add": {
        this.userService.addUser(userData).subscribe(
          (user: User) => {
            this.toastrService.success(
              this.translate.instant("UTILS.SUCCESS"),
              this.translate.instant("UTILS.SUCCESS_INFO", {
                subject: this.translate.instant("USERS.USER_EDIT.USER"),
                operation: this.translate.instant("UTILS.ADDED"),
              })
            );
            this.windowRef.close({
              user: user,
              operation: "add",
            });
          },
          (error) => {
            this.toastrService.warning(
              this.translate.instant("UTILS.WARNING"),
              this.translate.instant("UTILS.ERROR_INFO", {
                subject: this.translate.instant("USERS.USER_EDIT.USER"),
                operation: this.translate.instant("UTILS.ADDING"),
              })
            );
          }
        );
				break;
			}
			case "edit": {
				this.userService.updateUser(userData).subscribe(
          (user: User) => {
            this.toastrService.success(
              this.translate.instant("UTILS.SUCCESS"),
              this.translate.instant("UTILS.SUCCESS_INFO", {
                subject: this.translate.instant("USERS.USER_EDIT.USER"),
                operation: this.translate.instant("UTILS.EDITED"),
              })
            );
            this.user = user;
            this.windowRef.close({
              user: user,
              operation: "edit",
            });
          },
          (error) => {
            this.toastrService.warning  (
              this.translate.instant("UTILS.WARNING"),
              this.translate.instant("UTILS.ERROR_INFO", {
                subject: this.translate.instant("USERS.USER_EDIT.USER"),
                operation: this.translate.instant("UTILS.EDITING"),
              })
            );
          }
        );
				break;
			}
			case "delete": {
				this.userService.deleteUser(res.user.id).subscribe((user) => {
          this.toastrService.success(
            this.translate.instant("UTILS.SUCCESS"),
            this.translate.instant("UTILS.SUCCESS_INFO", {
              subject: this.translate.instant("USERS.USER_EDIT.USER"),
              operation: this.translate.instant("UTILS.DELETED"),
            })
          );
          this.windowRef.close({
            user: user,
            operation: "delete",
          });
        }, (error) => {
          this.toastrService.warning  (
            this.translate.instant("UTILS.WARNING"),
            this.translate.instant("UTILS.ERROR_INFO", {
              subject: this.translate.instant("USERS.USER_EDIT.USER"),
              operation: this.translate.instant("UTILS.DELETING"),
            })
          );
        });
        break;
      }
			default: {
        console.log('NO CHANGES');
				this.windowRef.close();
			}
		}
	}

  onRestaurantsSelected(restaurant_ids: string[]): void {
    const prevRestaurantIds = this.editedUser.restaurant_ids || [];
    const prevLocationIds = new Set(this.editedUser.location_ids || []);
    
    const hasAllSelected = restaurant_ids.includes('all');

    let finalRestaurantIds: string[];

    if (hasAllSelected) {
      finalRestaurantIds = this.restaurants.map(r => r.id);
      restaurant_ids = finalRestaurantIds;
    } else {
      finalRestaurantIds = restaurant_ids;
    }

    const newSelectedRestaurants = this.restaurants.filter(r =>
      finalRestaurantIds.includes(r.id)
    );

    const removedRestaurants = this.restaurants.filter(r =>
      prevRestaurantIds.includes(r.id) && !finalRestaurantIds.includes(r.id)
    );

    const updatedLocationIds = new Set(prevLocationIds);

    for (const restaurant of newSelectedRestaurants) {
      const locationIds = (restaurant.locations || []).map(loc => loc.id);
      const hasAnyLocation = locationIds.some(id => updatedLocationIds.has(id));
      const allKey = `${restaurant.id}:all`;

      if (!updatedLocationIds.has(allKey) && !hasAnyLocation) {
        updatedLocationIds.add(allKey);
      }
    }

    for (const restaurant of removedRestaurants) {
      const allKey = `${restaurant.id}:all`;
      updatedLocationIds.delete(allKey);
      (restaurant.locations || []).forEach(loc => updatedLocationIds.delete(loc.id));
    }

    this.editedUser.restaurant_ids = finalRestaurantIds;
    this.selectedRestaurants = newSelectedRestaurants;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.editedUser.location_ids = Array.from(updatedLocationIds);
      if (!this.editedUser.location_ids.length) {
        this.locationSelect.selectionModel = [];
      }
      this.cdr.detectChanges();
    });
  }

  compareFn = (a: any, b: any): boolean => {
    return a === b;
  };


  onLocationsSelected(location_ids: string[]): void {
    const newLocationIds = new Set<string>(location_ids);

    for (const restaurant of this.selectedRestaurants) {
      const allKey = `${restaurant.id}:all`;
      const locationIdsOfRestaurant = (restaurant.locations || []).map(loc => loc.id);

      const isAllNowSelected = newLocationIds.has(allKey);
      const prevHadAll = this.editedUser.location_ids?.includes(allKey);
      const hasAnyLocationSelected = locationIdsOfRestaurant.some(id => newLocationIds.has(id));

      if (isAllNowSelected && !prevHadAll) {
        for (const locId of locationIdsOfRestaurant) {
          newLocationIds.delete(locId);
        }
      }

      if (isAllNowSelected && hasAnyLocationSelected) {
        newLocationIds.delete(allKey);
      }

      const hasSelected = locationIdsOfRestaurant.some(id => newLocationIds.has(id));
      if (!hasSelected && !newLocationIds.has(allKey)) {
        newLocationIds.add(allKey);
      }
    }

    this.editedUser.location_ids = Array.from(newLocationIds);
  }



  getControlElementSize() {
    if (window.innerHeight < 400 || window.innerWidth < 400) {
      return "tiny";
    }
    if (window.innerHeight < 600 || window.innerWidth < 600) {
      return "small";
    }
    return "medium";
  }

  getSubject(subject: string) {
    return this.translate.instant(subject);
  }

  get isPasswordValid(): boolean {
    return this.editedUser?.password?.length >= 8;
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
